import React, { useState, useEffect, useMemo } from 'react'
import ReactLoading from 'react-loading'
import { useSelector, useDispatch } from 'react-redux'
import axios from 'axios'
import Overlay from '../../overlay'
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../../../Slices/auth'
import mode from '../../Mode'
import { GenerateFamilyV2Schema } from '../../../schemas'
import * as Yup from 'yup'

// Custom styles for enhanced presentation
const customStyles = `
    .family-card:hover {
        transform: translateY(-2px);
        transition: all 0.3s ease;
    }
    
    .method-card {
        transition: all 0.3s ease;
        border: 2px solid transparent !important;
    }
    
    .method-card:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
    }
    
    .method-card.selected {
        border-color: #0d6efd !important;
        box-shadow: 0 4px 15px rgba(13,110,253,0.2) !important;
    }
    
    .btn-group .btn {
        transition: all 0.3s ease;
        min-width: 140px;
    }
    
    .btn-group .btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(13,110,253,0.2);
    }
    
    .btn-check:checked + .btn {
        background: linear-gradient(45deg, #0d6efd, #0a58ca) !important;
        border-color: #0d6efd !important;
        color: white !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(13,110,253,0.3);
    }
    
    .btn-check:checked + .btn small {
        color: rgba(255,255,255,0.8) !important;
    }
    
    .stat-card {
        transition: all 0.3s ease;
    }
    
    .stat-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0,0,0,0.1) !important;
    }
    
    .gradient-btn {
        background: linear-gradient(45deg, #0b4c7a, #29b8b0) !important;
        border: none !important;
        position: relative;
        overflow: hidden;
    }
    
    .gradient-btn:hover {
        background: linear-gradient(45deg, #29b8b0, #0b4c7a) !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 15px rgba(11,76,122,0.3) !important;
    }
    
    .table-hover tbody tr:hover {
        background-color: #f8f9fa !important;
        transform: scale(1.01);
        transition: all 0.2s ease;
    }
    
    .icon-circle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
    }
    
    @media (max-width: 768px) {
        .container-fluid {
            padding-left: 15px;
            padding-right: 15px;
        }
        
        .table-responsive {
            font-size: 0.85rem;
        }
        
        .card-body {
            padding: 1rem !important;
        }
        
        .btn-group {
            flex-direction: column !important;
            width: 100%;
        }
        
        .btn-group .btn {
            min-width: auto;
            width: 100%;
            border-radius: 0.375rem !important;
            margin-bottom: 0.5rem;
        }
        
        .btn-group .btn:last-child {
            margin-bottom: 0;
        }
        
        .d-flex.justify-content-between {
            flex-direction: column !important;
            gap: 1rem;
        }
    }
    
    @media (max-width: 576px) {
        .btn-group .btn .text-start div {
            font-size: 0.9rem;
        }
        
        .btn-group .btn small {
            font-size: 0.75rem;
        }
    }
`;

const GenerateFamilyV2 = () => {
    const token = useSelector((state) => state.auth.token)
    const userType = useSelector((state) => state.auth.userType)
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(false)
    const baseurl = mode ? 'https://backend.portal.jkusdachurch.org/': 'http://127.0.0.1:8000/';
    const [data, setData] = useState({})
    const [familiesList, setFamiliesList] = useState([])
    const [indexList, setIndexList] = useState([])
    const [parentsList, setParentsList] = useState([])
    const [femReported, setFemReported] = useState(' ')
    const [maleReported, setMaleReported] = useState(' ')
    const [generationType, setGenerationType] = useState('excel') // 'excel' or 'database'

    const dispatch = useDispatch()

    const config = useMemo(() => {
        const configObj = {
            headers: {
                'Content-Type': 'application/json'
            }
        }
        if (token) {
            configObj.headers['Authorization'] = `Token ${token}`;
        }
        return configObj;
    }, [token])

    useEffect(() => {
        if (!(userType.includes('Nurturing Admin') || userType.includes('Super Admin'))) {
            navigate('/dashboard')
        }
        
        // Load existing data from database for comparison
        axios.get(`${baseurl}nurturing/all-families/`, config).then((resp) => {
            setData(resp.data)
            setFamiliesList(resp.data.families_list)
            setIndexList(resp.data.indexList)
            setParentsList(resp.data.parents)
            setFemReported(resp.data.femReported)
            setMaleReported(resp.data.maleReported)
        }).catch((err) => {
            console.error(err)
            if (err.response?.status === 401) {
                dispatch(logoutUser())
                navigate('/login', { state: { reason: 'Unauthorized' } })
            }
        })
    }, [userType, navigate, baseurl, config, dispatch])

    const validationSchema = useMemo(() => {
        if (generationType === 'excel') {
            return GenerateFamilyV2Schema.shape({
                file: Yup.mixed()
                    .required('File is required for Excel generation')
                    .test('fileType', 'Only Excel files are allowed', (value) => {
                        if (!value) return false;
                        return value.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                               value.type === 'application/vnd.ms-excel';
                    })
            });
        } else {
            return GenerateFamilyV2Schema;
        }
    }, [generationType])

    const onSubmit = async (values) => {
        setIsLoading(true)
        
        try {
            if (generationType === 'excel') {
                // V2 Generation from Excel file
                const formData = new FormData();
                formData.append('file', values.file);
                formData.append('number_of_families', values.numberOfFamilies);
                formData.append('document_type', values.documentType);
                formData.append('semester_title', values.semesterTitle);

                const response = await fetch(`${baseurl}nurturing/v2/randomize/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Token ${token}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const contentType = response.headers.get('content-type');
                
                if (contentType && contentType.includes('application/json')) {
                    // Handle JSON response
                    const data = await response.json();
                    console.log('Semester:', data.semester_title);
                    console.log('Families:', data.families);
                    setData(data);
                    if (data.families_list) setFamiliesList(data.families_list);
                    if (data.indexList) setIndexList(data.indexList);
                    if (data.parents) setParentsList(data.parents);
                } else {
                    // Handle file download
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `family_distribution_${values.semesterTitle.replace(/\s+/g, '_')}.${values.documentType === 'excel' ? 'xlsx' : 'csv'}`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                }
            } else {
                // Existing generation from database
                const response = await axios.post(`${baseurl}nurturing/randomize/`, {
                    number_of_families: values.numberOfFamilies,
                    semester_title: values.semesterTitle
                }, config);
                
                setData(response.data);
                setFamiliesList(response.data.families_list);
                setIndexList(response.data.indexList);
                setParentsList(response.data.parents);
            }
        } catch (error) {
            console.error('Error:', error);
            if (error.response?.status === 401) {
                dispatch(logoutUser())
                navigate('/login', { state: { reason: 'Unauthorized' } })
            }
        } finally {
            setIsLoading(false);
        }
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue } = useFormik({
        initialValues: {
            numberOfFamilies: '',
            semesterTitle: '',
            documentType: 'excel',
            file: null
        },
        validationSchema,
        onSubmit
    })

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setFieldValue('file', file);
    }

    const handleGenerationTypeChange = (type) => {
        setGenerationType(type);
        setFieldValue('file', null); // Reset file when switching types
    }

    return (
        <>
            <style>{customStyles}</style>
            <div id="App">
                <Overlay />
                <div className="main-content">
                    <div className="container-fluid py-4">            

                        {/* Statistics Row */}
                        <div className="row mb-4">
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="card border-0 shadow-sm h-100 stat-card">
                                    <div className="card-body text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <div className="bg-primary rounded-circle p-3 me-3 icon-circle">
                                                <i className="fas fa-users text-white"></i>
                                            </div>
                                            <div>
                                                <h3 className="mb-0 text-primary fw-bold">{femReported + maleReported}</h3>
                                                <small className="text-muted">Total Reported</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="card border-0 shadow-sm h-100 stat-card">
                                    <div className="card-body text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <div className="bg-info rounded-circle p-3 me-3 icon-circle">
                                                <i className="fas fa-male text-white"></i>
                                            </div>
                                            <div>
                                                <h3 className="mb-0 text-info fw-bold">{maleReported}</h3>
                                                <small className="text-muted">Male</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="card border-0 shadow-sm h-100 stat-card">
                                    <div className="card-body text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <div className="bg-success rounded-circle p-3 me-3 icon-circle">
                                                <i className="fas fa-female text-white"></i>
                                            </div>
                                            <div>
                                                <h3 className="mb-0 text-success fw-bold">{femReported}</h3>
                                                <small className="text-muted">Female</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="card border-0 shadow-sm h-100 stat-card">
                                    <div className="card-body text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <div className="bg-warning rounded-circle p-3 me-3 icon-circle">
                                                <i className="fas fa-home text-white"></i>
                                            </div>
                                            <div>
                                                <h3 className="mb-0 text-warning fw-bold">{familiesList.length}</h3>
                                                <small className="text-muted">Families Generated</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Generation Type Selection - Compact */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body py-3">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap">
                                            <h6 className="mb-0" style={{ color: '#0b4c7a' }}>
                                                <i className="fas fa-cogs me-2"></i>
                                                Generation Method
                                            </h6>
                                            <div className="btn-group" role="group" aria-label="Generation method selection">
                                                <input 
                                                    type="radio" 
                                                    className="btn-check" 
                                                    name="generationType" 
                                                    id="excel-option" 
                                                    checked={generationType === 'excel'}
                                                    onChange={() => handleGenerationTypeChange('excel')}
                                                />
                                                <label 
                                                    className="btn btn-outline-primary d-flex align-items-center px-3 py-2" 
                                                    htmlFor="excel-option"
                                                >
                                                    <i className="fas fa-file-excel me-2"></i>
                                                    <div className="text-start">
                                                        <div className="fw-semibold">Excel File</div>
                                                        <small className="text-muted">Upload data</small>
                                                    </div>
                                                </label>

                                                <input 
                                                    type="radio" 
                                                    className="btn-check" 
                                                    name="generationType" 
                                                    id="database-option" 
                                                    checked={generationType === 'database'}
                                                    onChange={() => handleGenerationTypeChange('database')}
                                                />
                                                <label 
                                                    className="btn btn-outline-primary d-flex align-items-center px-3 py-2" 
                                                    htmlFor="database-option"
                                                >
                                                    <i className="fas fa-database me-2"></i>
                                                    <div className="text-start">
                                                        <div className="fw-semibold">Database</div>
                                                        <small className="text-muted">Existing records</small>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form Section */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0 py-3">
                                        <h5 className="mb-0" style={{ color: '#0b4c7a' }}>
                                            <i className={`fas ${generationType === 'excel' ? 'fa-file-excel' : 'fa-database'} me-2`}></i>
                                            {generationType === 'excel' ? 'Generate Families (Excel)' : 'Generate Families (Database)'}
                                        </h5>
                                    </div>
                                    <div className="card-body p-4">
                                        <form onSubmit={handleSubmit}>
                                            <div className="row">
                                                {/* Semester Title */}
                                                <div className="col-md-6 mb-4">
                                                    <label className='form-label fw-semibold'>
                                                        <i className="fas fa-calendar-alt me-2 text-primary"></i>
                                                        Semester Title
                                                    </label>
                                                    <input
                                                        type='text'
                                                        className={`form-control form-control-lg ${errors.semesterTitle && touched.semesterTitle ? 'is-invalid' : ''}`}
                                                        name='semesterTitle'
                                                        value={values.semesterTitle}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="e.g., Fall 2025, Spring 2026"
                                                    />
                                                    {errors.semesterTitle && touched.semesterTitle && (
                                                        <div className='invalid-feedback'>{errors.semesterTitle}</div>
                                                    )}
                                                </div>

                                                {/* Number of Families */}
                                                <div className="col-md-6 mb-4">
                                                    <label className='form-label fw-semibold'>
                                                        <i className="fas fa-home me-2 text-primary"></i>
                                                        Number Of Families
                                                    </label>
                                                    <input
                                                        type='number'
                                                        className={`form-control form-control-lg ${errors.numberOfFamilies && touched.numberOfFamilies ? 'is-invalid' : ''}`}
                                                        name='numberOfFamilies'
                                                        value={values.numberOfFamilies}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        min="1"
                                                        max="100"
                                                        placeholder="Enter number of families"
                                                    />
                                                    {errors.numberOfFamilies && touched.numberOfFamilies && (
                                                        <div className='invalid-feedback'>{errors.numberOfFamilies}</div>
                                                    )}
                                                </div>

                                                {/* File Upload (only for Excel generation) */}
                                                {generationType === 'excel' && (
                                                    <div className="col-md-6 mb-4">
                                                        <label className='form-label fw-semibold'>
                                                            <i className="fas fa-upload me-2 text-primary"></i>
                                                            Excel File
                                                        </label>
                                                        <input
                                                            type='file'
                                                            className={`form-control form-control-lg ${errors.file && touched.file ? 'is-invalid' : ''}`}
                                                            accept=".xlsx,.xls"
                                                            onChange={handleFileChange}
                                                        />
                                                        {errors.file && touched.file && (
                                                            <div className='invalid-feedback'>{errors.file}</div>
                                                        )}
                                                        <div className="form-text">
                                                            <i className="fas fa-info-circle me-1"></i>
                                                            Upload an Excel file containing member data
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Document Type (only for Excel generation) */}
                                                {generationType === 'excel' && (
                                                    <div className="col-md-6 mb-4">
                                                        <label className='form-label fw-semibold'>
                                                            <i className="fas fa-download me-2 text-primary"></i>
                                                            Download Format
                                                        </label>
                                                        <select
                                                            className={`form-select form-control-lg ${errors.documentType && touched.documentType ? 'is-invalid' : ''}`}
                                                            name='documentType'
                                                            value={values.documentType}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        >
                                                            <option value="excel">Excel (.xlsx)</option>
                                                            <option value="csv">CSV (.csv)</option>
                                                        </select>
                                                        {errors.documentType && touched.documentType && (
                                                            <div className='invalid-feedback'>{errors.documentType}</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Submit Button */}
                                            <div className="d-flex justify-content-center mt-4">
                                                <button
                                                    className="btn btn-primary btn-lg px-5 py-3 rounded-pill shadow-sm gradient-btn"
                                                    type="submit"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? (
                                                        <>
                                                            <ReactLoading type="spin" color="#fff" height={20} width={20} />
                                                            <span className="ms-2">Processing...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className={`fas ${generationType === 'excel' ? 'fa-download' : 'fa-cogs'} me-2`}></i>
                                                            {generationType === 'excel' ? 'Generate & Download' : 'Generate Families'}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Display */}
                    {familiesList.length > 0 && (
                        <div className="container-fluid">
                            <div className="row mb-4">
                                <div className="col-12">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-header bg-white border-0 py-3">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <h5 className="mb-0" style={{ color: '#0b4c7a' }}>
                                                    <i className="fas fa-list-ul me-2"></i>
                                                    Generated Families ({familiesList.length} families)
                                                </h5>
                                                <span className="badge bg-success fs-6">
                                                    <i className="fas fa-check-circle me-1"></i>
                                                    Generation Complete
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {familiesList.map((fm, index) => (
                                <div key={index} className="row mb-4">
                                    <div className="col-12">
                                        <div className="card border-0 shadow-sm family-card">
                                            <div className="card-header" style={{ backgroundColor: '#f8f9fa' }}>
                                                <h5 className="mb-0 d-flex align-items-center">
                                                    <div className="bg-primary rounded-circle p-2 me-3 icon-circle">
                                                        <i className="fas fa-home text-white"></i>
                                                    </div>
                                                    <span style={{ color: '#0b4c7a' }}>Family {fm}</span>
                                                </h5>
                                            </div>
                                            <div className="card-body p-0">
                                                <div className="table-responsive">
                                                    <table className="table table-hover mb-0" style={{ fontSize: '0.95rem' }}>
                                                        <thead style={{ backgroundColor: '#29b8b0', color: 'white' }}>
                                                            <tr>
                                                                <th className="py-3 ps-4">
                                                                    <i className="fas fa-user me-2"></i>Name
                                                                </th>
                                                                <th className="py-3">
                                                                    <i className="fas fa-tag me-2"></i>Role
                                                                </th>
                                                                <th className="py-3">
                                                                    <i className="fas fa-graduation-cap me-2"></i>Year Group
                                                                </th>
                                                                <th className="py-3">
                                                                    <i className="fas fa-phone me-2"></i>Phone Number
                                                                </th>
                                                                <th className="py-3">
                                                                    <i className="fas fa-venus-mars me-2"></i>Gender
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {parentsList.map((parent, parentIndex) => {
                                                                if (parent.number === fm) {
                                                                    return (
                                                                        <React.Fragment key={parentIndex}>
                                                                            {parent.dad && (
                                                                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                                                    <td className="py-3 ps-4">
                                                                                        <div className="d-flex align-items-center">
                                                                                            <div className="bg-info rounded-circle p-2 me-3" style={{ width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                                <i className="fas fa-male text-white"></i>
                                                                                            </div>
                                                                                            <strong>{parent.dad.member.name}</strong>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="py-3">
                                                                                        <span className="badge bg-primary">Dad</span>
                                                                                    </td>
                                                                                    <td className="py-3"><strong>{parent.dad.member.yos}</strong></td>
                                                                                    <td className="py-3">
                                                                                        <a href={`tel:${parent.dad.phone.member_phone}`} className="text-decoration-none">
                                                                                            <strong>{parent.dad.phone.member_phone}</strong>
                                                                                        </a>
                                                                                    </td>
                                                                                    <td className="py-3">
                                                                                        <span className="badge bg-info">Male</span>
                                                                                    </td>
                                                                                </tr>
                                                                            )}
                                                                            {parent.mom && (
                                                                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                                                    <td className="py-3 ps-4">
                                                                                        <div className="d-flex align-items-center">
                                                                                            <div className="bg-success rounded-circle p-2 me-3" style={{ width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                                <i className="fas fa-female text-white"></i>
                                                                                            </div>
                                                                                            <strong>{parent.mom.member.name}</strong>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="py-3">
                                                                                        <span className="badge bg-success">Mom</span>
                                                                                    </td>
                                                                                    <td className="py-3"><strong>{parent.mom.member.yos}</strong></td>
                                                                                    <td className="py-3">
                                                                                        <a href={`tel:${parent.mom.phone.member_phone}`} className="text-decoration-none">
                                                                                            <strong>{parent.mom.phone.member_phone}</strong>
                                                                                        </a>
                                                                                    </td>
                                                                                    <td className="py-3">
                                                                                        <span className="badge bg-success">Female</span>
                                                                                    </td>
                                                                                </tr>
                                                                            )}
                                                                        </React.Fragment>
                                                                    )
                                                                }
                                                                return null;
                                                            })}
                                                            {indexList.map((i, dataIndex) => {
                                                                if (data.family && data.family[i] === fm) {
                                                                    return (
                                                                        <tr key={dataIndex} className="border-top">
                                                                            <td className="py-3 ps-4">
                                                                                <div className="d-flex align-items-center">
                                                                                    <div className="bg-warning rounded-circle p-2 me-3" style={{ width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                        <i className="fas fa-user-graduate text-white"></i>
                                                                                    </div>
                                                                                    {data.name ? data.name[i] : ''}
                                                                                </div>
                                                                            </td>
                                                                            <td className="py-3">
                                                                                <span className="badge bg-warning">Student</span>
                                                                            </td>
                                                                            <td className="py-3">{data.intake ? data.intake[i] : ''}</td>
                                                                            <td className="py-3">
                                                                                {data.phone_number && data.phone_number[i] && (
                                                                                    <a href={`tel:${data.phone_number[i]}`} className="text-decoration-none">
                                                                                        {data.phone_number[i]}
                                                                                    </a>
                                                                                )}
                                                                            </td>
                                                                            <td className="py-3">
                                                                                {data.gender && data.gender[i] && (
                                                                                    <span className={`badge ${data.gender[i].toLowerCase() === 'male' ? 'bg-info' : 'bg-success'}`}>
                                                                                        {data.gender[i]}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }
                                                                return null;
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default GenerateFamilyV2